import React, { useState, useEffect } from 'react';
import { benefitsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Gift, Edit, Trash2, Star } from 'lucide-react';

export default function BenefitsPage() {
  const [benefits, setBenefits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingBenefit, setEditingBenefit] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    points_cost: 100,
    category: '',
    terms_conditions: '',
    image_url: ''
  });

  useEffect(() => {
    loadBenefits();
  }, []);

  const loadBenefits = async () => {
    try {
      const res = await benefitsAPI.getAll(null, false);
      setBenefits(res.data);
    } catch (error) {
      toast.error('Error al cargar beneficios');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      if (!formData.name || !formData.points_cost) {
        toast.error('Nombre y costo en puntos son requeridos');
        return;
      }
      await benefitsAPI.create(formData);
      toast.success('Beneficio creado exitosamente');
      setShowDialog(false);
      resetForm();
      loadBenefits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear beneficio');
    }
  };

  const handleUpdate = async () => {
    try {
      await benefitsAPI.update(editingBenefit.id, formData);
      toast.success('Beneficio actualizado');
      setEditingBenefit(null);
      setShowDialog(false);
      resetForm();
      loadBenefits();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este beneficio?')) return;
    try {
      await benefitsAPI.delete(id);
      toast.success('Beneficio eliminado');
      loadBenefits();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', points_cost: 100, category: '', terms_conditions: '', image_url: '' });
  };

  const openEditDialog = (benefit) => {
    setEditingBenefit(benefit);
    setFormData({
      name: benefit.name,
      description: benefit.description || '',
      points_cost: benefit.points_cost,
      category: benefit.category || '',
      terms_conditions: benefit.terms_conditions || '',
      image_url: benefit.image_url || ''
    });
    setShowDialog(true);
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Cargando...</div>;
  }

  return (
    <div className="space-y-6" data-testid="benefits-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Catálogo de Beneficios</h1>
          <p className="text-slate-600">Beneficios canjeables por los proveedores</p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) { setEditingBenefit(null); resetForm(); }
        }}>
          <DialogTrigger asChild>
            <Button data-testid="create-benefit-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Beneficio
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingBenefit ? 'Editar Beneficio' : 'Crear Nuevo Beneficio'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium">Nombre *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: Descuento 10%"
                  data-testid="benefit-name-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Descripción</label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descripción del beneficio"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Categoría</label>
                <Input
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="Ej: Descuentos, Regalos, Experiencias"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Costo en Puntos *</label>
                <Input
                  type="number"
                  value={formData.points_cost}
                  onChange={(e) => setFormData({ ...formData, points_cost: parseInt(e.target.value) || 0 })}
                  min={1}
                  data-testid="benefit-points-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Términos y Condiciones</label>
                <Input
                  value={formData.terms_conditions}
                  onChange={(e) => setFormData({ ...formData, terms_conditions: e.target.value })}
                  placeholder="Válido por 30 días..."
                />
              </div>
              <div>
                <label className="text-sm font-medium">URL de Imagen</label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <Button onClick={editingBenefit ? handleUpdate : handleCreate} className="w-full" data-testid="save-benefit-btn">
                {editingBenefit ? 'Actualizar' : 'Crear'} Beneficio
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Benefits Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {benefits.map(benefit => (
          <Card key={benefit.id} className={!benefit.is_active ? 'opacity-50' : ''} data-testid={`benefit-card-${benefit.id}`}>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                    {benefit.image_url ? (
                      <img src={benefit.image_url} alt={benefit.name} className="w-10 h-10 rounded-lg object-cover" />
                    ) : (
                      <Gift className="w-6 h-6 text-white" />
                    )}
                  </div>
                  <div>
                    <CardTitle className="text-lg">{benefit.name}</CardTitle>
                    {benefit.category && (
                      <Badge variant="outline" className="text-xs mt-1">{benefit.category}</Badge>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => openEditDialog(benefit)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(benefit.id)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-3">{benefit.description || 'Sin descripción'}</p>
              <div className="flex items-center gap-1 text-emerald-600 font-bold text-lg">
                <Star className="w-5 h-5 fill-current" />
                {benefit.points_cost} puntos
              </div>
              {benefit.terms_conditions && (
                <p className="text-xs text-slate-400 mt-2">{benefit.terms_conditions}</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {benefits.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          <Gift className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No hay beneficios en el catálogo</p>
          <p className="text-sm">Crea tu primer beneficio para comenzar</p>
        </div>
      )}
    </div>
  );
}
